// DEBUG: Log filters before removal
// DEBUG: Log filters after removal
import { h, Component } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { CioPlp, CioPlpGrid, Filters, Pagination } from '@constructor-io/constructorio-ui-plp';
import '@constructor-io/constructorio-ui-plp/styles.css';
import './plp-overrides.css';
import Storefront from 'utils/storefront';
import * as queries from 'utils/storefront/queries';
import { edgesToNodes } from 'utils/storefront/format-resource';
import ProductCard from '../utils/product-card';
import mapConstructorProduct from '../utils/map-constructor-product';

// Spinner overlay for loading states
function SpinnerOverlay({ minHeight = 200 }) {
  return (
    <div style={{ position: 'relative', minHeight }}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(255,255,255,0.7)',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'opacity 0.3s',
        }}
      >
        <div
          className="spinner"
          style={{
            width: 40,
            height: 40,
            border: '4px solid #ccc',
            borderTop: '4px solid #333',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }`}</style>
      </div>
    </div>
  );
}

// ErrorBoundary: Catches errors in Constructor grid and triggers fallback
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  componentDidCatch(error) {
    this.setState({ hasError: true });
    if (typeof this.props.onError === 'function') {
      this.props.onError(error);
    }
  }

  render(props, state) {
    return state.hasError ? props.fallback || null : props.children;
  }
}

const API_KEY = 'key_9cAuBaO6I0Z7gU7C';

// ShopifyGrid: Fallback grid using Shopify Storefront API
const ShopifyGrid = ({ collection, template, ...props }) => {
  const [products, setProducts] = useState(collection.products || []);
  const [loading, setLoading] = useState(!products.length);
  const [error, setError] = useState(null);

  // Fetch products if not provided
  useEffect(() => {
    if (!products.length) {
      setLoading(true);
      Storefront.getInstance()
        .request({
          query: queries.collectionGet,
          variables: { id: collection.id, first: 50 },
        })
        .then((response) => {
          const nodes = response.data?.collection?.products
            ? edgesToNodes(response.data.collection.products, 'product')
            : [];
          setProducts(nodes);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    }
  }, [collection.id, products.length]);

  if (loading) return props.isGridLoading ? null : <SpinnerOverlay minHeight={200} />;
  if (error || !products.length) return null;

  return (
    <div className="container">
      <div className="grid grid-cols-2 gap-x-12 gap-y-20 px-15 py-20 l:gap-20 l:pb-40 l:pr-0">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            template={template}
            {...props}
          />
        ))}
      </div>
    </div>
  );
};

// MyCustomProductCard: Maps Constructor product to Shopify card props
function MyCustomProductCard({ item, layout, saleCollection, ...props }) {
  const product = mapConstructorProduct(item);

  return (
    <ProductCard
      product={product}
      template={props?.template || 'collection'}
      saleCollection={saleCollection}
      layout={layout}
      openCustomizerStyleModal={props?.openCustomizerStyleModal}
      collectionHandle={props?.collectionHandle}
      {...props}
    />
  );
}

// ProductGrid: Main PLP component with Constructor grid and Shopify fallback
const ProductGrid = (props) => {
  const [constructorError, setConstructorError] = useState(false);
  const [constructorLoaded, setConstructorLoaded] = useState(false);
  const isDesktop = props?.layout?.isAboveBreakpoint('lg');

  // Fallback to Shopify if Constructor is unavailable or errors
  if (!API_KEY || constructorError) {
    return <ShopifyGrid {...props} />;
  }

  // Handles errors from Constructor grid
  const handleCioError = () => {
    setConstructorError(true);
    setConstructorLoaded(true);
  };

  // Prevent duplicate logs for the same response
  const lastResponseRef = useRef();
  // Smooth loading state for grid and key for remount
  const [isGridLoading, setIsGridLoading] = useState(false);
  const [plpKey, setPlpKey] = useState(0);

  useEffect(() => {
    const handler = () => {
      const currentUrl = new URL(window.location.href);
      const section = document.querySelector('[data-section="product-grid"]');
      const header = document.querySelector('.shopify-section-header');

      setIsGridLoading(true);
      setPlpKey((k) => k + 1);

      if (!isDesktop) {
        setFilterMenuVisible(false);
      }

      if (
        section &&
        currentUrl.searchParams.size > 0 &&
        !(currentUrl.searchParams.size === 1 && currentUrl.searchParams.has('q'))
      ) {
        const sectionRect = section.getBoundingClientRect();
        const headerHeight = header ? header.offsetHeight : 0;
        const scrollY = window.scrollY + sectionRect.top - headerHeight - 15;

        window.scrollTo({ top: scrollY, behavior: 'smooth' });
      }
    };

    // Listen for custom event
    window.addEventListener('constructor-url-change', handler);
    // Listen for browser navigation (back/forward)
    const popstateHandler = () => {
      window.dispatchEvent(new CustomEvent('constructor-url-change'));
    };
    window.addEventListener('popstate', popstateHandler);

    return () => {
      window.removeEventListener('constructor-url-change', handler);
      window.removeEventListener('popstate', popstateHandler);
    };
  }, []);

  // urlHelpers: only update the URL with the original collection handle (no suffix)
  const urlHelpers = {
    getUrlFromState: (state) => {
      // Always use the original handle for the URL
      const { collectionHandle } = props;
      const params = new URLSearchParams();
      const currentUrl = new URL(window.location.href);

      if (state.filters) {
        Object.entries(state.filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              params.append(`filters[${key}]`, v);
            });
          } else {
            params.append(`filters[${key}]`, value);
          }
        });
      }

      if (state.page) params.append('page', state.page);
      if (state.sortBy) params.append('sortBy', state.sortBy);

      const paramString = params.toString();

      // search results page handling
      if (currentUrl.pathname.includes('/search') && currentUrl.searchParams.has('q')) {
        const qValue = currentUrl.searchParams.get('q');
        const paramsValue = paramString ? `&${paramString}` : '';

        return `${window.location.origin}/search?q=${qValue}${paramsValue}`;
      }

      return paramString
        ? `${window.location.origin}/collections/${collectionHandle}?${paramString}`
        : `${window.location.origin}/collections/${collectionHandle}`;
    },
    setUrl: (url) => {
      if (typeof window === 'undefined') return;

      const currentUrl = new URL(window.location.href);
      const newUrl = new URL(url);

      if (!currentUrl.searchParams.has('q') || newUrl?.searchParams?.size > 0) {
        window.history.pushState(null, '', url);
        window.dispatchEvent(new CustomEvent('constructor-url-change'));
      }
    },
  };

  // Run setUrl on page load
  useEffect(() => {
    const initialState = {};
    const initialUrl = urlHelpers.getUrlFromState(initialState);
    urlHelpers.setUrl(initialUrl);
  }, []);

  // Filter menu visibility state (persisted in localStorage)
  const FILTER_MENU_KEY = 'productGridFilterMenuVisible';
  const getInitialFilterMenuVisible = () => {
    if (props.collectionHandle === 'customizer') return false;
    if (!isDesktop) return false;
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(FILTER_MENU_KEY);
    return stored === null ? true : stored === 'true';
  };
  const [filterMenuVisible, setFilterMenuVisible] = useState(getInitialFilterMenuVisible());
  // Prevent body scroll when filter menu is open on mobile
  // Set customizerq value from query string if present, even if input is rendered after mount
  useEffect(() => {
    if (props.collectionHandle === 'customizer' && typeof window !== 'undefined') {
      const currentUrl = new URL(window.location.href);
      const qValue = currentUrl.searchParams.get('q');
      if (!qValue) return;

      // Try to set immediately
      const setInputValue = () => {
        const input = document.getElementById('customizerq');
        if (input) input.value = qValue;
      };
      setInputValue();

      // Observe DOM for input if not present
      if (!document.getElementById('customizerq')) {
        const observer = new MutationObserver(() => {
          const input = document.getElementById('customizerq');
          if (input) {
            input.value = qValue;
            observer.disconnect();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
        // eslint-disable-next-line
        return () => observer.disconnect();
      }
    }
  }, [props.collectionHandle]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(FILTER_MENU_KEY, filterMenuVisible);
      if (!isDesktop && filterMenuVisible) {
        document.body.classList.add('scroll-lock--fixed');
      } else {
        document.body.classList.remove('scroll-lock--fixed');
      }
    }
    // Cleanup on unmount
    return () => {
      if (typeof window !== 'undefined') {
        document.body.classList.remove('scroll-lock--fixed');
      }
    };
  }, [filterMenuVisible, isDesktop]);
  const handleToggleFilterMenu = () => {
    setFilterMenuVisible((v) => !v);
  };
  // Overlay click handler for mobile
  const handleOverlayClick = () => {
    setFilterMenuVisible(false);
  };
  // Render Constructor grid, fallback to Shopify on error

  const segment = props.collectionHandle === 'customizer' ? 'customizer' : 'main_site';

  try {
    // Add filter for customizer collection to only get GAN products
    // const staticRequestConfigs =
    //   props.collectionHandle === 'customizer'
    //     ? { resultsPerPage: 60, filters: { tag_is_gan_product: ['True'] } }
    //     : { resultsPerPage: 60 };
    const staticRequestConfigs = { resultsPerPage: 60 };
    return (
      <ErrorBoundary
        fallback={<ShopifyGrid {...props} />}
        onError={handleCioError}
      >
        <div style={{ position: 'relative' }}>
          {isGridLoading && <SpinnerOverlay minHeight={200} />}
          {/* Mobile filter overlay */}
          {!isDesktop && filterMenuVisible && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                background: 'rgba(0,0,0,0.4)',
                zIndex: 100,
                transition: 'opacity 0.3s',
              }}
              onClick={handleOverlayClick}
            />
          )}
          <CioPlp
            key={plpKey}
            apiKey={API_KEY}
            cioClientOptions={{ segments: [segment] }}
            staticRequestConfigs={staticRequestConfigs}
            onError={handleCioError}
            urlHelpers={urlHelpers}
            collectionHandle={props.collectionHandle}
            {...props}
          >
            <CioPlpGrid>
              {(cioProps) => {
                // Pass urlHelpers from parent scope
                const urlHelpersRef = urlHelpers;
                try {
                  const { response } = cioProps.data || {};
                  if (response && response !== lastResponseRef.current) {
                    lastResponseRef.current = response;
                  }

                  // Hide loading overlay when data is ready
                  if (isGridLoading && cioProps.data) setIsGridLoading(false);

                  const { results } = response || {};
                  if (!cioProps.data) {
                    if (!constructorLoaded) setConstructorLoaded(true);
                    return isGridLoading ? null : <SpinnerOverlay minHeight={200} />;
                  }

                  const currentUrl = new URL(window.location.href);
                  const isSearch = currentUrl.pathname.includes('/search') && currentUrl.searchParams.has('q');
                  const searchTerm = currentUrl.searchParams.get('q') || '';

                  let filtersButtonLabel = filterMenuVisible
                    ? props.collectionHandle === 'customizer'
                      ? 'Hide Interests'
                      : 'Hide Filters'
                    : props.collectionHandle === 'customizer'
                      ? 'Show Interests'
                      : 'Show Filters';

                  if (!isDesktop) {
                    filtersButtonLabel = filtersButtonLabel.replace(/^(Hide |Show )/, '');
                  }

                  if (Array.isArray(results) && results.length === 0 && constructorLoaded) {
                    const gridSection = document.querySelector('[data-section="product-grid"]');
                    if (gridSection && gridSection.classList.contains('min-h-screen')) {
                      gridSection.classList.remove('min-h-screen');
                    }
                    return (
                      <div className="container flex flex-col items-center justify-center py-40">
                        <h2 style={{ fontSize: 22, fontWeight: 600, marginBottom: 16 }}>
                          {`No results found for "${searchTerm}"`}
                        </h2>
                        {props.collectionHandle === 'customizer' && (
                          <a
                            href="/collections/customizer"
                            style={{
                              textDecoration: 'underline',
                              fontWeight: 500,
                              fontSize: 16,
                            }}
                          >
                            Go back
                          </a>
                        )}
                      </div>
                    );
                  }

                  if (Array.isArray(results) && results.length > 0) {
                    return (
                      <div
                        className={`container flex w-full flex-col px-15 l:px-40 ${filterMenuVisible ? 'has-filterMenuVisible' : ''} ${currentUrl.pathname.includes('/search') && currentUrl.searchParams.has('q') ? 'lg:mt-40' : ''}`}
                        data-cnstrc-browse={!isSearch}
                        data-cnstrc-search={isSearch}
                        data-cnstrc-result-id={cioProps?.data?.resultId}
                        data-cnstrc-num-results={cioProps?.data?.response?.totalNumResults}
                        data-cnstrc-zero-result={cioProps?.data?.response?.totalNumResults === 0 ? 'true' : 'false'}
                        data-cnstrc-filter-name="group_id"
                        data-cnstrc-filter-value={props?.collectionHandle || ''}
                      >
                        <script
                          id="cnstrc-data"
                          type="application/json"
                        >
                          {JSON.stringify({
                            request: cioProps?.data?.request,
                            result_id: cioProps?.data?.resultId,
                          })}
                        </script>
                        <div className="cio-toolbar">
                          <button
                            type="button"
                            aria-label={filtersButtonLabel}
                            onClick={handleToggleFilterMenu}
                            style={{
                              border: '1px solid #393D3C',
                              background: 'transparent',
                              color: '#333',
                              padding: '6px 16px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontWeight: 500,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                              transition: 'background 0.2s',
                              minWidth: 120,
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                              <svg
                                width="13"
                                height="12"
                                viewBox="0 0 13 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ display: 'inline-block', verticalAlign: 'middle' }}
                              >
                                <line
                                  y1="2"
                                  x2="13"
                                  y2="2"
                                  stroke="#393d3c"
                                ></line>
                                <circle
                                  cx="9.5"
                                  cy="2"
                                  r="1"
                                  fill="#393d3c"
                                  stroke="#393d3c"
                                ></circle>
                                <line
                                  y1="6"
                                  x2="13"
                                  y2="6"
                                  stroke="#393d3c"
                                ></line>
                                <circle
                                  cx="3.5"
                                  cy="6"
                                  r="1"
                                  fill="#393d3c"
                                  stroke="#393d3c"
                                ></circle>
                                <line
                                  y1="10"
                                  x2="13"
                                  y2="10"
                                  stroke="#393d3c"
                                ></line>
                                <circle
                                  cx="9.5"
                                  cy="10"
                                  r="1"
                                  fill="#393d3c"
                                  stroke="#393d3c"
                                ></circle>
                              </svg>
                              {filtersButtonLabel}
                            </span>
                          </button>
                          {currentUrl.pathname.includes('/search') && currentUrl.searchParams.has('q') ? (
                            <h2 className="cio-plp-search-results-text">{`Search results for "${currentUrl.searchParams.get('q')}"`}</h2>
                          ) : (
                            ''
                          )}
                          {props.collectionHandle === 'customizer' && (
                            <form
                              name="customizerSearch"
                              role="search"
                              action="/collections/customizer"
                              method="get"
                              noValidate
                              className="c-form c-form--minWidth u-border u-border--bottom u-border--trblSmallViewport js-search"
                            >
                              <fieldset>
                                <legend className="u-hiddenAccessible">Search custom products</legend>
                                <label
                                  id="customizerqlabel"
                                  className="u-hiddenAccessible"
                                >
                                  Search custom products
                                </label>
                                <input
                                  type="text"
                                  id="customizerq"
                                  name="q"
                                  aria-labelledby="customizerqlabel"
                                  placeholder="Search Customizer"
                                  aria-invalid="false"
                                  className="c-formField c-formField--borderNone c-formField--boxShadowNone c-formField--small u-marginBottomNone u-paddingTopNone c-formField--customizerSearch qa-input--customizerSearch--customizerSearch valid"
                                />
                                <button
                                  type="submit"
                                  className="c-icon c-icon--formField"
                                  value="Search custom products"
                                  aria-label="Search"
                                >
                                  <svg
                                    width="21px"
                                    height="20px"
                                    viewBox="0 0 21 20"
                                    version="1.1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="c-icon__svg is-svg"
                                  >
                                    <title>Search</title>
                                    <g
                                      stroke="none"
                                      strokeWidth="1"
                                      fill="none"
                                      fillRule="evenodd"
                                    >
                                      <g
                                        transform="translate(-879.000000, -59.000000)"
                                        fill="#0A4875"
                                        fillRule="nonzero"
                                      >
                                        <g transform="translate(461.500000, 51.000000)">
                                          <path
                                            fill="#000"
                                            d="M437.724636,25.2912801 L433.830485,21.3971292 C433.654721,21.2213651 433.416463,21.1237184 433.166488,21.1237184 L432.529831,21.1237184 C433.607851,19.7449468 434.248413,18.0107411 434.248413,16.1242066 C434.248413,11.6363636 430.61205,8 426.124207,8 C421.636364,8 418,11.6363636 418,16.1242066 C418,20.6120496 421.636364,24.2484132 426.124207,24.2484132 C428.010741,24.2484132 429.744947,23.6078508 431.123718,22.5298311 L431.123718,23.1664876 C431.123718,23.4164632 431.221365,23.6547212 431.397129,23.8304853 L435.29128,27.7246363 C435.658432,28.0917879 436.252124,28.0917879 436.61537,27.7246363 L437.72073,26.6192755 C438.087882,26.2521238 438.087882,25.6584318 437.724636,25.2912801 Z M426.124207,21.1237184 C423.362758,21.1237184 421.124695,18.8895616 421.124695,16.1242066 C421.124695,13.3627575 423.358852,11.1246949 426.124207,11.1246949 C428.885656,11.1246949 431.123718,13.3588517 431.123718,16.1242066 C431.123718,18.8856557 428.889562,21.1237184 426.124207,21.1237184 Z"
                                            style="/* --tw-shadow-colored: red; *//* fill: red; */"
                                          ></path>
                                        </g>
                                      </g>
                                    </g>
                                  </svg>
                                </button>
                              </fieldset>
                            </form>
                          )}
                          <span
                            className="body-14"
                            style={{ fontWeight: 500 }}
                          >
                            {cioProps.data.response.totalNumResults?.toLocaleString()} Products
                          </span>
                        </div>
                        {/* Mobile: Show selected filters above grid */}
                        {cioProps.filters?.facets &&
                          cioProps.filters.facets.some((facet) =>
                            facet.options.some((opt) => opt.status === 'selected'),
                          ) && (
                            <div
                              className="cio-selected-filters-mobile"
                              style={{
                                margin: '16px 0',
                                padding: '8px',
                                background: '#f6f6f6',
                                borderRadius: '6px',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                alignItems: 'center',
                              }}
                            >
                              <button
                                type="button"
                                style={{
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '4px 12px',
                                  fontSize: '13px',
                                  color: '#333',
                                  cursor: 'pointer',
                                  fontWeight: 500,
                                  marginRight: '8px',
                                  textDecoration: 'underline',
                                }}
                                aria-label="Clear all filters"
                                onClick={() => {
                                  const newState = {
                                    filters: {},
                                    page: 1,
                                  };
                                  if (cioProps.data?.request?.sortBy) {
                                    newState.sortBy = cioProps.data.request.sortBy;
                                  }
                                  const newUrl = urlHelpersRef.getUrlFromState(newState);
                                  urlHelpersRef.setUrl(newUrl);
                                }}
                              >
                                Clear All
                              </button>
                              {cioProps.filters.facets.flatMap((facet) =>
                                facet.options
                                  .filter((opt) => opt.status === 'selected')
                                  .map((opt, idx) => (
                                    <span
                                      key={facet.name + opt.value + idx}
                                      style={{
                                        background: '#e0e0e0',
                                        borderRadius: '4px',
                                        padding: '4px 10px',
                                        fontSize: '13px',
                                        color: '#333',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        cursor: 'pointer',
                                      }}
                                      aria-label={`Clear ${facet.displayName}: ${opt.displayName}`}
                                      onClick={() => {
                                        // Remove the selected filter from URL and refresh grid
                                        // Build filters object from selected facets/options
                                        const filters = {};
                                        cioProps.filters.facets.forEach((f) => {
                                          const selectedOptions = f.options.filter((o) => o.status === 'selected');
                                          if (selectedOptions.length > 0) {
                                            // If multi-select, use array; if single, use value
                                            if (selectedOptions.length > 1) {
                                              filters[f.name] = selectedOptions.map((o) => o.value);
                                            } else {
                                              filters[f.name] = selectedOptions[0].value;
                                            }
                                          }
                                        });
                                        // Remove the clicked filter
                                        if (Array.isArray(filters[facet.name])) {
                                          const filtered = filters[facet.name].filter((v) => v !== opt.value);
                                          if (filtered.length > 0) {
                                            filters[facet.name] = filtered;
                                          } else {
                                            delete filters[facet.name];
                                          }
                                        } else if (typeof filters[facet.name] !== 'undefined') {
                                          if (filters[facet.name] === opt.value) {
                                            delete filters[facet.name];
                                          }
                                        }
                                        // Build new state for urlHelpers using only updated filters and page
                                        const newState = {
                                          filters: { ...filters },
                                          page: 1, // Reset to first page
                                        };
                                        // DEBUG: Log filters after removal
                                        // Optionally preserve sortBy if present
                                        if (cioProps.data?.request?.sortBy) {
                                          newState.sortBy = cioProps.data.request.sortBy;
                                        }
                                        // Remove any undefined filters
                                        Object.keys(newState.filters).forEach((key) => {
                                          if (
                                            newState.filters[key] === undefined ||
                                            (Array.isArray(newState.filters[key]) && newState.filters[key].length === 0)
                                          ) {
                                            delete newState.filters[key];
                                          }
                                        });
                                        const newUrl = urlHelpersRef.getUrlFromState(newState);
                                        urlHelpersRef.setUrl(newUrl);
                                      }}
                                    >
                                      {facet.displayName}: {opt.displayName}
                                      <span
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#666',
                                          fontSize: '14px',
                                          marginLeft: '4px',
                                          pointerEvents: 'none',
                                        }}
                                      >
                                        &#10005;
                                      </span>
                                    </span>
                                  )),
                              )}
                            </div>
                          )}

                        <div className="w-full lg:flex lg:flex-row lg:items-start">
                          <div
                            className="cio-filters-container"
                            style={{ display: filterMenuVisible ? 'block' : 'none' }}
                          >
                            {/* Mobile close X button */}
                            {!isDesktop && filterMenuVisible && (
                              <button
                                aria-label="Close filters"
                                onClick={() => setFilterMenuVisible(false)}
                                style={{
                                  position: 'absolute',
                                  top: 5,
                                  right: 15,
                                  zIndex: 101,
                                  background: 'none',
                                  border: 'none',
                                  fontSize: 20,
                                  color: '#333',
                                  cursor: 'pointer',
                                }}
                              >
                                &#10005;
                              </button>
                            )}
                            <Filters facets={cioProps.filters.facets} />
                          </div>
                          <div className="w-full flex-1">
                            <div className="cio-product-tiles-container">
                              {results.map((item, idx) => (
                                <MyCustomProductCard
                                  item={item}
                                  key={item.item_id || idx}
                                  template={cioProps.template}
                                  layout={props.layout}
                                  saleCollection={props.saleCollection || 'false'}
                                  collectionHandle={props.collectionHandle}
                                  {...cioProps}
                                />
                              ))}
                            </div>
                            {cioProps.data.response.totalNumResults > 60 && (
                              <Pagination
                                resultsPerPage={60}
                                totalNumResults={cioProps.data.response.totalNumResults}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  if (constructorLoaded) {
                    return (
                      <ShopifyGrid
                        {...props}
                        isGridLoading={isGridLoading}
                      />
                    );
                  }

                  return isGridLoading ? null : <SpinnerOverlay minHeight={200} />;
                } catch {
                  setConstructorError(true);
                  setConstructorLoaded(true);
                  return <ShopifyGrid {...props} />;
                }
              }}
            </CioPlpGrid>
          </CioPlp>
        </div>
      </ErrorBoundary>
    );
  } catch {
    setConstructorError(true);
    setConstructorLoaded(true);

    return <ShopifyGrid {...props} />;
  }
};

export default ProductGrid;
